import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import api from '../services/api';
import type { Order, OrderAction, OrderItem, OrderMetaData, OrderTrackingItem } from '../types/myaccount';
import { LoadingBar, SummaryCardsSkeleton, TableSkeleton } from './MyAccountLoadingStates';
import MyAccountCard from './MyAccountCard';
import MyAccountPageShell from './MyAccountPageShell';
import { formatMoneyWithCurrencyCode } from '../utils/formatMoneyWithCurrencyCode';
import { getStatusBadgeModifierClass } from '../utils/statusBadges';

interface ViewOrderProps {
    displayMode?: 'order-details' | 'order-received';
    featureFlags?: {
        orderHistoryTrackingEnabled?: boolean;
    };
}

interface ParsedMetaField {
    label: string;
    value: string;
}

interface ParsedFeeLine {
    key: string;
    label: string;
    amount: number;
    meta: string;
}

interface TotalRow {
    key: string;
    label: string;
    amount: number;
    isTotal?: boolean;
    meta?: string;
}

const INTERNAL_ITEM_META_EXCLUSIONS = new Set([
    '_warehouse_id',
    '_warehouse_address',
    '_warehouse_name',
    '_weka_discount_code',
    '_weka_discount_unique_id',
    '_weka_stock_factor',
    '_weka_stock_base_qty',
    '_weka_stock_bucket_id',
]);

const ITEM_META_LABEL_OVERRIDES: Record<string, string> = {
    _uom: 'UOM',
    uom: 'UOM',
    _serial_number: 'Serial Number',
};

const normalizeMetaLabelForCompare = (label: string): string =>
    label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const normalizeMetaValueForCompare = (value: string): string => {
    const trimmed = value.trim();
    const numericCandidate = trimmed.replace(/,/g, '');

    if (/^-?\d+(?:\.\d+)?$/.test(numericCandidate)) {
        const numericValue = Number(numericCandidate);
        if (Number.isFinite(numericValue)) {
            return String(numericValue);
        }
    }

    return trimmed.toLowerCase();
};

const normalizeMetaKeyForCompare = (key: string): string =>
    key
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');

const FEE_DISCOUNT_CODE_META_KEYS = new Set([
    'erpdiscountcode',
    'discountcode',
    'wekadiscountcode',
]);

const FEE_DISCOUNT_UNIQUE_ID_META_KEYS = new Set([
    'erpdiscountuniqueid',
    'discountuniqueid',
    'wekadiscountuniqueid',
]);

const FRONTEND_ITEM_META_EXCLUSION_PATTERNS = [
    'prediscount',
    'prediscountunitprice',
];

const ViewOrder: React.FC<ViewOrderProps> = ({ featureFlags, displayMode = 'order-details' }) => {
    const { orderId } = useParams<{ orderId: string }>();
    const location = useLocation();
    const [order, setOrder] = useState<Order | null>(null);
    const [trackingItems, setTrackingItems] = useState<OrderTrackingItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [legacyContent, setLegacyContent] = useState<string>('');
    const [legacyLoading, setLegacyLoading] = useState(false);

    const numericOrderId = useMemo(() => Number(orderId), [orderId]);
    const isOrderReceivedMode = displayMode === 'order-received';
    const trackingEnabled = featureFlags?.orderHistoryTrackingEnabled === true;

    useEffect(() => {
        if (!orderId || Number.isNaN(numericOrderId) || numericOrderId <= 0) {
            setError('Invalid order ID provided.');
            setLoading(false);
            return;
        }

        loadOrderData(numericOrderId);
    }, [orderId, numericOrderId, trackingEnabled]);

    const loadOrderData = async (resolvedOrderId: number) => {
        try {
            setLoading(true);
            setError(null);
            setLegacyContent('');

            const orderResponse = await api.getOrder(resolvedOrderId);
            setOrder(orderResponse);

            if (trackingEnabled) {
                try {
                    const trackingResponse = await api.getOrderTracking(resolvedOrderId);
                    setTrackingItems(Array.isArray(trackingResponse) ? trackingResponse : []);
                } catch (_trackingError) {
                    // Tracking should not block order details rendering.
                    setTrackingItems([]);
                }
            } else {
                setTrackingItems([]);
            }
        } catch (err) {
            console.error('Error loading order details:', err);
            setError(err instanceof Error ? err.message : 'Failed to load order details');
        } finally {
            setLoading(false);
        }
    };

    const loadLegacyContent = async () => {
        if (!orderId) {
            return;
        }

        try {
            setLegacyLoading(true);
            if (isOrderReceivedMode) {
                const params = new URLSearchParams();
                params.set('action', 'weka_my_account_commerce_render');
                params.set('type', 'order_received');
                params.set('order_id', orderId);

                const orderKey = new URLSearchParams(location.search).get('key');
                if (orderKey) {
                    params.set('key', orderKey);
                }

                const response = await fetch(`/wp-admin/admin-ajax.php?${params.toString()}`, {
                    credentials: 'same-origin',
                });
                const payload = await response.json() as {
                    success?: boolean;
                    data?: { html?: string; message?: string } | string;
                    message?: string;
                };

                if (!response.ok || payload.success !== true || typeof payload.data !== 'object' || payload.data === null) {
                    const message = (typeof payload?.data === 'object' && payload?.data !== null && typeof payload.data.message === 'string')
                        ? payload.data.message
                        : (typeof payload?.message === 'string' ? payload.message : 'Failed to load classic order receipt.');
                    throw new Error(message);
                }

                const html = typeof payload.data.html === 'string' ? payload.data.html : '';
                setLegacyContent(html);
                return;
            }

            const response = await fetch(
                `/wp-admin/admin-ajax.php?action=get_view_order_content&order_id=${encodeURIComponent(orderId)}`,
                { credentials: 'same-origin' }
            );

            if (!response.ok) {
                throw new Error('Failed to load classic order view.');
            }

            const html = await response.text();
            setLegacyContent(html);
        } catch (err) {
            console.error('Error loading legacy order view:', err);
            setError(err instanceof Error ? err.message : (
                isOrderReceivedMode ? 'Failed to load classic order receipt' : 'Failed to load classic order view'
            ));
        } finally {
            setLegacyLoading(false);
        }
    };

    const formatDate = (dateString?: string): string => {
        if (!dateString) {
            return 'N/A';
        }

        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return 'N/A';
        }

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatMoney = (amount?: string | number): string => {
        return formatMoneyWithCurrencyCode(
            amount,
            order?.currency,
            order?.currency_display_mode ?? order?.show_currency_code,
            order?.currency_symbol,
        );
    };

    const getProductUrl = (productId?: number): string => {
        const resolvedId = Number(productId || 0);
        if (!Number.isFinite(resolvedId) || resolvedId <= 0) {
            return '';
        }
        return `/?post_type=product&p=${resolvedId}`;
    };

    const normalizeStatus = (rawStatus?: string): string => {
        if (!rawStatus) {
            return 'Unknown';
        }

        return rawStatus
            .split('-')
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');
    };

    const getStatusBadgeClass = (status?: string): string => getStatusBadgeModifierClass('orders', status);

    const parseMetaValue = (value: unknown): string => {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        if (Array.isArray(value)) {
            return value.map((entry) => parseMetaValue(entry)).filter(Boolean).join(', ');
        }

        if (typeof value === 'object') {
            const record = value as Record<string, unknown>;
            if (typeof record.display === 'string') {
                return record.display;
            }
            if (typeof record.value === 'string') {
                return record.value;
            }

            try {
                return JSON.stringify(value);
            } catch (_error) {
                return '';
            }
        }

        return '';
    };

    const parseNumericAmount = (value: unknown): number => {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }

        const parsed = parseFloat(String(value || '0').replace(/,/g, ''));
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const parseFeeLineMeta = (metaData: OrderMetaData[]): { discountCode: string; discountUniqueId: string } => {
        let discountCode = '';
        let discountUniqueId = '';

        metaData.forEach((meta) => {
            const rawKey = String(meta.display_key || meta.key || '').trim();
            if (rawKey === '') {
                return;
            }

            const normalizedKey = normalizeMetaKeyForCompare(rawKey);
            const parsedValue = parseMetaValue(meta.display_value ?? meta.value);
            if (!parsedValue) {
                return;
            }

            if (!discountCode && FEE_DISCOUNT_CODE_META_KEYS.has(normalizedKey)) {
                discountCode = parsedValue;
                return;
            }

            if (!discountUniqueId && FEE_DISCOUNT_UNIQUE_ID_META_KEYS.has(normalizedKey)) {
                discountUniqueId = parsedValue;
            }
        });

        return { discountCode, discountUniqueId };
    };

    const parseFeeLines = (): ParsedFeeLine[] => {
        const feeLines = Array.isArray(order?.fee_lines) ? order.fee_lines : [];

        return feeLines
            .map((feeLine, index): ParsedFeeLine | null => {
                const amount = parseNumericAmount(feeLine.total);
                const label = String(feeLine.name || '').trim() || 'Fee';
                const metaData = Array.isArray(feeLine.meta_data) ? feeLine.meta_data : [];
                const { discountCode, discountUniqueId } = parseFeeLineMeta(metaData);

                const metaParts: string[] = [];
                if (discountCode) {
                    metaParts.push(`Code: ${discountCode}`);
                }
                if (discountUniqueId) {
                    metaParts.push(`Unique ID: ${discountUniqueId}`);
                }

                if (Math.abs(amount) < 0.000001 && label === 'Fee' && metaParts.length === 0) {
                    return null;
                }

                return {
                    key: `${feeLine.id || index}`,
                    label,
                    amount,
                    meta: metaParts.join(' | '),
                };
            })
            .filter((feeLine): feeLine is ParsedFeeLine => feeLine !== null);
    };

    const getLineItemMetaFields = (lineItem: OrderItem): ParsedMetaField[] => {
        const metaData = Array.isArray(lineItem.meta_data) ? lineItem.meta_data : [];

        const parsed = metaData
            .map((meta: OrderMetaData) => {
                const rawKey = String(meta.key || '').trim();
                if (!rawKey) {
                    return null;
                }

                const displayKey = String(meta.display_key || '').trim();
                const combinedMetaKey = normalizeMetaKeyForCompare(`${rawKey} ${displayKey}`);
                const isFrontendExcluded = FRONTEND_ITEM_META_EXCLUSION_PATTERNS.some((pattern) => combinedMetaKey.includes(pattern));
                if (isFrontendExcluded) {
                    return null;
                }

                const normalizedKey = rawKey.toLowerCase();
                const isInternal = rawKey.startsWith('_') && !ITEM_META_LABEL_OVERRIDES[normalizedKey];
                if (isInternal || INTERNAL_ITEM_META_EXCLUSIONS.has(normalizedKey)) {
                    return null;
                }

                const parsedValue = parseMetaValue(meta.display_value ?? meta.value);
                if (!parsedValue) {
                    return null;
                }

                const label = ITEM_META_LABEL_OVERRIDES[normalizedKey]
                    || String(meta.display_key || rawKey)
                        .replace(/^_+/, '')
                        .replace(/[_-]+/g, ' ')
                        .replace(/\b\w/g, (ch) => ch.toUpperCase());

                return {
                    label,
                    value: parsedValue,
                };
            })
            .filter((entry): entry is ParsedMetaField => entry !== null);

        const seen = new Set<string>();
        return parsed.filter((entry) => {
            const dedupeKey = `${normalizeMetaLabelForCompare(entry.label)}::${normalizeMetaValueForCompare(entry.value)}`;
            if (seen.has(dedupeKey)) {
                return false;
            }
            seen.add(dedupeKey);
            return true;
        });
    };

    const getOrderMetaValue = (metaKey: string): string => {
        const metaData = Array.isArray(order?.meta_data) ? order?.meta_data : [];
        const target = metaData.find((meta) => String(meta.key || '') === metaKey);
        if (!target) {
            return '';
        }

        return parseMetaValue(target.value);
    };

    const pickupStoreName = getOrderMetaValue('_pickup_store_name');
    const pickupStoreAddress = getOrderMetaValue('_pickup_store_address');
    const pickupStoreHours = getOrderMetaValue('_pickup_store_hours');

    const warehouseName = getOrderMetaValue('_warehouse_name');
    const warehouseAddress = getOrderMetaValue('_warehouse_address');

    const orderActions = (): OrderAction[] => {
        if (!order) {
            return [];
        }

        if (Array.isArray(order.account_actions) && order.account_actions.length > 0) {
            return order.account_actions.filter((action) => action && action.url && action.key !== 'view');
        }

        if (order.reorder_url) {
            return [{
                key: 'order_again',
                label: 'Order again',
                url: order.reorder_url,
                icon: 'fas fa-redo',
            }];
        }

        return [];
    };

    if (loading) {
        return (
            <div className="weka-my-account-page weka-my-account-page--detail">
                <MyAccountCard
                    variant="page"
                    slug={isOrderReceivedMode ? 'order-received-loading' : 'view-order-loading'}
                    className={`view-order-card ${isOrderReceivedMode ? 'is-order-received' : ''}`}
                    innerClassName="view-order-body"
                    wrapContent={false}
                >
                        <div className="view-order-header">
                            <div>
                                <LoadingBar height="16px" width="144px" style={{ marginBottom: '12px' }} />
                                <LoadingBar height="30px" width="48%" style={{ marginBottom: '10px' }} />
                                <LoadingBar height="16px" width="32%" style={{ marginBottom: '8px' }} />
                                {isOrderReceivedMode && (
                                    <LoadingBar height="14px" width="24%" />
                                )}
                            </div>
                            <LoadingBar height="32px" width="112px" style={{ borderRadius: '999px' }} />
                        </div>

                        <div className={`view-order-summary-grid ${isOrderReceivedMode ? 'is-received' : ''}`}>
                            <SummaryCardsSkeleton count={isOrderReceivedMode ? 4 : 3} minHeight="88px" />
                        </div>

                        <div className="view-order-section">
                            <LoadingBar height="20px" width="12%" style={{ marginBottom: '16px' }} />
                            <TableSkeleton columns={3} rows={4} />
                        </div>

                        <div className="view-order-section view-order-totals-section">
                            <LoadingBar height="20px" width="14%" style={{ marginBottom: '16px' }} />
                            <div className="skeleton" style={{ height: '176px', borderRadius: '10px' }} />
                        </div>

                        <div className="view-order-section view-order-address-grid">
                            <div className="view-order-detail-card">
                                <LoadingBar height="18px" width="40%" style={{ marginBottom: '14px' }} />
                                <LoadingBar height="14px" width="78%" style={{ marginBottom: '10px' }} />
                                <LoadingBar height="14px" width="62%" style={{ marginBottom: '10px' }} />
                                <LoadingBar height="14px" width="70%" />
                            </div>
                            <div className="view-order-detail-card">
                                <LoadingBar height="18px" width="42%" style={{ marginBottom: '14px' }} />
                                <LoadingBar height="14px" width="74%" style={{ marginBottom: '10px' }} />
                                <LoadingBar height="14px" width="58%" style={{ marginBottom: '10px' }} />
                                <LoadingBar height="14px" width="66%" />
                            </div>
                        </div>
                </MyAccountCard>
            </div>
        );
    }

    if (error && !order) {
        return (
            <MyAccountPageShell
                title={isOrderReceivedMode ? 'Order Received' : 'Order Details'}
                subtitle={isOrderReceivedMode
                    ? 'Review the receipt and next steps for your order.'
                    : 'Review items, totals, and delivery information for this order.'}
            >
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <button onClick={() => loadOrderData(numericOrderId)} className="btn btn-primary">
                            Retry
                        </button>
                        <button onClick={loadLegacyContent} className="btn btn-outline-secondary" disabled={legacyLoading}>
                            {legacyLoading
                                ? 'Loading…'
                                : (isOrderReceivedMode ? 'Load classic receipt' : 'Load classic order view')}
                        </button>
                        <Link to="/orders" className="button">
                            Back to Order History
                        </Link>
                    </div>
                    {legacyContent && (
                        <div className="view-order-fallback mt-4" dangerouslySetInnerHTML={{ __html: legacyContent }} />
                    )}
            </MyAccountPageShell>
        );
    }

    if (!order) {
        return (
            <MyAccountPageShell
                title={isOrderReceivedMode ? 'Order Received' : 'Order Details'}
                subtitle={isOrderReceivedMode
                    ? 'Review the receipt and next steps for your order.'
                    : 'Review items, totals, and delivery information for this order.'}
            >
                    <div className="alert alert-warning" role="alert">
                        Order not found.
                    </div>
                    <Link to="/orders" className="button">
                        Back to Order History
                    </Link>
            </MyAccountPageShell>
        );
    }

    const isInvoicePaymentOrder = order.is_invoice_payment === true;
    const isPendingApprovalOrder = order.status === 'pending-approval';
    const showPendingApprovalReceipt = isOrderReceivedMode && isPendingApprovalOrder;
    const invoicePaymentRefs = Array.isArray(order.paid_invoice_ids)
        ? order.paid_invoice_ids.filter((invoiceId): invoiceId is string => typeof invoiceId === 'string' && invoiceId.trim() !== '')
        : [];
    const backLinkTarget = isInvoicePaymentOrder ? '/invoices?tab=payments' : '/orders';
    const backLinkLabel = isInvoicePaymentOrder ? 'Back to Invoice Payments' : 'Back to Order History';
    const detailEntityLabel = isInvoicePaymentOrder ? 'Invoice Payment' : 'Order';
    const lineItems = Array.isArray(order.line_items) ? order.line_items : (Array.isArray(order.items) ? order.items : []);
    const subtotal = lineItems.reduce((sum, item) => sum + parseNumericAmount(item.subtotal ?? item.total), 0);
    const shippingTotal = parseNumericAmount(order.shipping_total);
    const discountTotal = parseNumericAmount(order.discount_total);
    const taxTotal = parseNumericAmount(order.total_tax);
    const feeLines = parseFeeLines();
    const totalRows: TotalRow[] = [
        {
            key: 'subtotal',
            label: 'Subtotal',
            amount: subtotal,
        },
    ];

    if (discountTotal > 0.000001) {
        totalRows.push({
            key: 'line-discount',
            label: 'Discount',
            amount: -1 * discountTotal,
        });
    }

    feeLines.forEach((feeLine) => {
        totalRows.push({
            key: `fee-${feeLine.key}`,
            label: feeLine.label,
            amount: feeLine.amount,
            meta: feeLine.meta,
        });
    });

    totalRows.push(
        {
            key: 'shipping',
            label: 'Shipping',
            amount: shippingTotal,
        },
        {
            key: 'tax',
            label: 'Tax',
            amount: taxTotal,
        },
        {
            key: 'total',
            label: 'Total',
            amount: parseNumericAmount(order.total),
            isTotal: true,
        }
    );

    const paymentMethod = order.payment_method_title || 'N/A';
    const backorderShippingPreference = typeof order.backorder_shipping_preference === 'string'
        ? order.backorder_shipping_preference.trim()
        : '';
    const orderNumber = order.order_number || String(order.id);
    const formattedOrderDate = formatDate(order.date_created);
    const formattedAppliedAt = order.invoice_payment_applied_at ? formatDate(order.invoice_payment_applied_at) : 'Not yet';

    return (
        <div className="weka-my-account-page weka-my-account-page--detail">
            <MyAccountCard
                variant="page"
                slug={isOrderReceivedMode ? 'order-received' : 'view-order'}
                className={`view-order-card ${isOrderReceivedMode ? 'is-order-received' : ''}`}
                innerClassName="view-order-body"
                wrapContent={false}
            >
                <div className="view-order-header">
                    <div>
                        <Link to={backLinkTarget} className="view-order-back-link weka-account-link">
                            <i className="fas fa-arrow-left" aria-hidden="true"></i>
                            {backLinkLabel}
                        </Link>
                        {isOrderReceivedMode && (
                            <p className="view-order-received-eyebrow">
                                {showPendingApprovalReceipt ? 'Approval Requested' : 'Order Received'}
                            </p>
                        )}
                        <h2 className="view-order-title">
                            {isOrderReceivedMode
                                ? (showPendingApprovalReceipt
                                    ? 'Thank you. Your order has been sent for approval.'
                                    : 'Thank you. Your order has been received.')
                                : `${detailEntityLabel} #${orderNumber}`}
                        </h2>
                        <p className="view-order-subtitle">
                            {isOrderReceivedMode
                                ? (showPendingApprovalReceipt
                                    ? `Order #${orderNumber} is waiting for an approver on your account to review it.`
                                    : `${detailEntityLabel} #${orderNumber}`)
                                : `${isInvoicePaymentOrder ? 'Created' : 'Placed'} on ${formattedOrderDate}`}
                        </p>
                        {isOrderReceivedMode && (
                            <p className="view-order-received-reference">
                                Placed on {formattedOrderDate}
                            </p>
                        )}
                    </div>
                    <span className={`view-order-status ${getStatusBadgeClass(order.status)}`}>
                        {normalizeStatus(order.status)}
                    </span>
                </div>

                {showPendingApprovalReceipt && (
                    <div className="alert alert-info" role="status">
                        Your order has been submitted successfully and is now pending approval. It will move to processing once an approver from your company reviews it.
                    </div>
                )}

                <div className={`view-order-summary-grid ${isOrderReceivedMode ? 'is-received' : ''}`}>
                    {isOrderReceivedMode && (
                        <div className="view-order-summary-card">
                            <span className="label">{showPendingApprovalReceipt ? 'Approval Status' : `${detailEntityLabel} Number`}</span>
                            <strong>{showPendingApprovalReceipt ? 'Pending Approval' : `#${orderNumber}`}</strong>
                        </div>
                    )}
                    <div className="view-order-summary-card">
                        <span className="label">{isInvoicePaymentOrder ? 'Created Date' : 'Order Date'}</span>
                        <strong>{formattedOrderDate}</strong>
                    </div>
                    {isOrderReceivedMode && order.billing?.email && (
                        <div className="view-order-summary-card">
                            <span className="label">Email</span>
                            <strong>{order.billing.email}</strong>
                        </div>
                    )}
                    <div className="view-order-summary-card">
                        <span className="label">Payment Method</span>
                        <strong>{paymentMethod}</strong>
                    </div>
                    {backorderShippingPreference && (
                        <div className="view-order-summary-card">
                            <span className="label">Backorder Shipping</span>
                            <strong>{backorderShippingPreference}</strong>
                        </div>
                    )}
                    <div className="view-order-summary-card">
                        <span className="label">{isInvoicePaymentOrder ? 'Payment Total' : 'Order Total'}</span>
                        <strong>{formatMoney(order.total)}</strong>
                    </div>
                </div>

                {isInvoicePaymentOrder && (
                    <div className="view-order-section">
                        <h3>Invoice Payment Summary</h3>
                        <div className="view-order-summary-grid">
                            <div className="view-order-summary-card">
                                <span className="label">Invoices</span>
                                <strong>
                                    {invoicePaymentRefs.length > 0 ? (
                                        <span className="d-flex flex-wrap gap-2">
                                            {invoicePaymentRefs.map((invoiceRef) => (
                                                <Link
                                                    key={invoiceRef}
                                                    to={`/invoices/${encodeURIComponent(invoiceRef)}`}
                                                    className="weka-account-link text-decoration-none"
                                                >
                                                    {invoiceRef}
                                                </Link>
                                            ))}
                                        </span>
                                    ) : (
                                        'None linked'
                                    )}
                                </strong>
                            </div>
                            <div className="view-order-summary-card">
                                <span className="label">Applied</span>
                                <strong>{formattedAppliedAt}</strong>
                            </div>
                        </div>
                    </div>
                )}

                <div className="view-order-section">
                    <h3>Items</h3>
                    <div className="table-responsive">
                        <table className="table view-order-items-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Qty</th>
                                    <th className="text-end">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems.map((item) => {
                                    const metaFields = getLineItemMetaFields(item);

                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="view-order-item-name">
                                                    {item.product_id > 0 ? (
                                                        <a href={getProductUrl(item.product_id)} className="weka-account-link">{item.name}</a>
                                                    ) : (
                                                        item.name
                                                    )}
                                                </div>
                                                {metaFields.length > 0 && (
                                                    <ul className="view-order-item-meta">
                                                        {metaFields.map((field, index) => (
                                                            <li key={`${item.id}-${field.label}-${index}`}>
                                                                <span>{field.label}:</span> {field.value}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </td>
                                            <td>{item.quantity}</td>
                                            <td className="text-end">{formatMoney(item.total)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="view-order-section view-order-totals-section">
                    <h3>Totals</h3>
                    <dl>
                        {totalRows.map((row) => (
                            <div key={row.key} className={row.isTotal ? 'is-total' : ''}>
                                <dt>
                                    <span>{row.label}</span>
                                    {row.meta && <small className="view-order-total-meta">{row.meta}</small>}
                                </dt>
                                <dd>{formatMoney(row.amount)}</dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {trackingEnabled && trackingItems.length > 0 && (
                    <div className="view-order-section">
                        <h3>Tracking</h3>
                        <ul className="view-order-tracking-list">
                            {trackingItems.map((item, index) => (
                                <li key={`${item.tracking_number || 'tracking'}-${index}`}>
                                    <div>
                                        <strong>{item.tracking_number || 'Tracking available'}</strong>
                                        {item.date_shipped && <span>Shipped: {formatDate(item.date_shipped)}</span>}
                                    </div>
                                    {item.custom_tracking_link && (
                                        <a href={item.custom_tracking_link} target="_blank" rel="noopener noreferrer" className="weka-account-link">
                                            Track shipment
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {(pickupStoreName || warehouseName) && (
                    <div className="view-order-section view-order-fulfillment-grid">
                        {pickupStoreName && (
                            <div className="view-order-detail-card">
                                <h4>Pickup Details</h4>
                                <p><strong>{pickupStoreName}</strong></p>
                                {pickupStoreAddress && <p>{pickupStoreAddress}</p>}
                                {pickupStoreHours && <p>{pickupStoreHours}</p>}
                            </div>
                        )}

                        {warehouseName && (
                            <div className="view-order-detail-card">
                                <h4>Fulfillment Location</h4>
                                <p><strong>{warehouseName}</strong></p>
                                {warehouseAddress && <p>{warehouseAddress}</p>}
                            </div>
                        )}
                    </div>
                )}

                <div className="view-order-section view-order-address-grid">
                    <div className="view-order-detail-card">
                        <h4>Billing Address</h4>
                        <p>{order.billing?.first_name} {order.billing?.last_name}</p>
                        <p>{order.billing?.company}</p>
                        <p>{order.billing?.address_1}</p>
                        {order.billing?.address_2 && <p>{order.billing.address_2}</p>}
                        <p>{order.billing?.city} {order.billing?.state} {order.billing?.postcode}</p>
                        <p>{order.billing?.country}</p>
                        {order.billing?.phone && <p>{order.billing.phone}</p>}
                        {order.billing?.email && <p>{order.billing.email}</p>}
                    </div>

                    <div className="view-order-detail-card">
                        <h4>Shipping Address</h4>
                        <p>{order.shipping?.first_name} {order.shipping?.last_name}</p>
                        <p>{order.shipping?.company}</p>
                        <p>{order.shipping?.address_1}</p>
                        {order.shipping?.address_2 && <p>{order.shipping.address_2}</p>}
                        <p>{order.shipping?.city} {order.shipping?.state} {order.shipping?.postcode}</p>
                        <p>{order.shipping?.country}</p>
                    </div>
                </div>

                {orderActions().length > 0 && (
                    <div className="view-order-section">
                        <h3>Actions</h3>
                        <div className="view-order-actions">
                            {orderActions().map((action) => (
                                <a
                                    key={`${action.key}-${action.url}`}
                                    href={action.url}
                                    className="button weka-secondary-btn"
                                >
                                    {action.label}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-warning mt-3" role="alert">
                        {error}
                    </div>
                )}

                {legacyContent && (
                    <div className="view-order-section">
                        <h3>{isOrderReceivedMode ? 'Classic Receipt Fallback' : 'Classic View Fallback'}</h3>
                        <div className="view-order-fallback" dangerouslySetInnerHTML={{ __html: legacyContent }} />
                    </div>
                )}
            </MyAccountCard>
        </div>
    );
};

export default ViewOrder;
